import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const emptyForm = {
  id: null,
  student: "",
  adjustment_type: "SIBLING_DISCOUNT",
  academic_year: "",
  term: "",
  currency: "",
  amount: "",
  reason: "",
  notes: "",
  is_active: true,
};

export default function FeeAdjustmentsPanel({ academicYears = [], terms = [], currencies = [], onChanged }) {
  const [loading, setLoading] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [options, setOptions] = useState({ students: [], types: [] });
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");

  const activeCurrencies = useMemo(() => currencies.filter((item) => item.is_active), [currencies]);
  const availableTerms = useMemo(
    () => terms.filter((item) => !form.academic_year || String(item.academic_year) === String(form.academic_year)),
    [terms, form.academic_year]
  );
  const visibleStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options.students;
    return options.students.filter((item) =>
      `${item.name} ${item.student_number} ${item.class_group}`.toLowerCase().includes(q)
    );
  }, [options.students, search]);

  const load = async () => {
    try {
      setLoading(true);
      const [adjustmentsResponse, optionsResponse] = await Promise.all([
        axiosInstance.get("fees/adjustments/"),
        axiosInstance.get("fees/adjustments/options/"),
      ]);
      setAdjustments(adjustmentsResponse.data || []);
      setOptions(optionsResponse.data || { students: [], types: [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load fee discounts and exemptions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = { ...form, amount: Number(form.amount) };
      if (form.id) {
        await axiosInstance.patch(`fees/adjustments/${form.id}/`, payload);
        toast.success("Fee adjustment updated.");
      } else {
        await axiosInstance.post("fees/adjustments/", payload);
        toast.success("Fee adjustment added.");
      }
      setForm(emptyForm);
      await load();
      onChanged?.();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.detail || Object.values(data || {})?.flat?.()?.[0] || "Failed to save fee adjustment.");
    } finally {
      setLoading(false);
    }
  };

  const edit = (item) => setForm({
    id: item.id,
    student: item.student,
    adjustment_type: item.adjustment_type,
    academic_year: item.academic_year,
    term: item.term,
    currency: item.currency,
    amount: item.amount,
    reason: item.reason || "",
    notes: item.notes || "",
    is_active: item.is_active,
  });

  const remove = async (item) => {
    if (!window.confirm(`Remove ${item.adjustment_type_display} for ${item.student_name}?`)) return;
    try {
      setLoading(true);
      await axiosInstance.delete(`fees/adjustments/${item.id}/`);
      toast.success("Fee adjustment removed.");
      if (form.id === item.id) setForm(emptyForm);
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove fee adjustment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white">
        <h5 className="mb-1 fw-bold">Fee Discounts & Exemptions</h5>
        <small className="text-muted">These adjustments are automatically deducted when a new invoice is generated for the selected year and term.</small>
      </div>
      <div className="card-body">
        <div className="alert alert-light border">
          Use this for sibling discounts, staff benefits, pastor discounts and approved exemptions. Do not create negative fee categories.
        </div>

        <form className="row g-3 mb-4" onSubmit={save}>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Find Student</label>
            <input className="form-control mb-2" placeholder="Search name, admission no. or class" value={search} onChange={(e) => setSearch(e.target.value)} />
            <select className="form-select" value={form.student} onChange={(e) => setForm((prev) => ({ ...prev, student: e.target.value }))} required>
              <option value="">Select student</option>
              {visibleStudents.map((item) => (
                <option key={item.id} value={item.id}>{item.student_number} - {item.name} ({item.class_group})</option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Adjustment Type</label>
            <select className="form-select" value={form.adjustment_type} onChange={(e) => setForm((prev) => ({ ...prev, adjustment_type: e.target.value }))} required>
              {(options.types || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Academic Year</label>
            <select className="form-select" value={form.academic_year} onChange={(e) => setForm((prev) => ({ ...prev, academic_year: e.target.value, term: "" }))} required>
              <option value="">Select academic year</option>
              {academicYears.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Term</label>
            <select className="form-select" value={form.term} onChange={(e) => setForm((prev) => ({ ...prev, term: e.target.value }))} required>
              <option value="">Select term</option>
              {availableTerms.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Currency</label>
            <select className="form-select" value={form.currency} onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value }))} required>
              <option value="">Select currency</option>
              {activeCurrencies.map((item) => <option key={item.id} value={item.id}>{item.code} - {item.name}</option>)}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Amount</label>
            <input type="number" min="0.01" step="0.01" className="form-control" value={form.amount} onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))} required />
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold">Reason</label>
            <input className="form-control" value={form.reason} onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))} placeholder="e.g. Approved sibling discount" />
          </div>
          <div className="col-md-4 d-flex align-items-end">
            <label className="form-check form-switch mb-2">
              <input className="form-check-input" type="checkbox" checked={Boolean(form.is_active)} onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))} />
              <span className="form-check-label fw-semibold">Active</span>
            </label>
          </div>
          <div className="col-12 d-flex gap-2">
            <button className="btn btn-success" disabled={loading}>{loading ? "Saving..." : form.id ? "Update Adjustment" : "Add Adjustment"}</button>
            {form.id && <button type="button" className="btn btn-light border" onClick={() => setForm(emptyForm)}>Cancel Edit</button>}
          </div>
        </form>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light"><tr><th>Student</th><th>Type</th><th>Period</th><th>Amount</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {adjustments.length ? adjustments.map((item) => (
                <tr key={item.id}>
                  <td><div className="fw-semibold">{item.student_name}</div><small className="text-muted">{item.student_number}</small></td>
                  <td>{item.adjustment_type_display}</td>
                  <td>{item.academic_year_name} - {item.term_name}</td>
                  <td>{item.currency_code} {item.amount}</td>
                  <td><span className={`badge ${item.is_active ? "text-bg-success" : "text-bg-secondary"}`}>{item.is_active ? "Active" : "Inactive"}</span></td>
                  <td><div className="d-flex gap-1"><button className="btn btn-sm btn-light border" onClick={() => edit(item)}><i className="bi bi-pencil-square text-success"></i></button><button className="btn btn-sm btn-outline-danger" onClick={() => remove(item)}><i className="bi bi-trash"></i></button></div></td>
                </tr>
              )) : <tr><td colSpan="6" className="text-center text-muted py-4">No discounts or exemptions recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
